import { formatRelative } from "date-fns";
import { RecordContextProvider, useGetList, useListContext, useTranslate } from "ra-core";
import { type MouseEvent, useCallback, useMemo } from "react";
import { Link } from "react-router";
import { Checkbox } from "@/components/ui/checkbox";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { PageSpinner } from '@/components/ui/page-spinner';

import { EtiquetaTemperatura } from "./EtiquetaTemperatura";
import { useProductModule } from "@/lib/useProductModule";
import { useDateFnsLocale } from "../misc/useDateFnsLocale";
import type { Contact } from "../types";
import { getContactDetail, getContactDisplayName } from "./contact-details";
import { cabecalhoTabelaNorth } from "../bento/ui";
import { useSomenteLeitura } from "../commons/somenteLeitura";
import { useMascara } from "../valores-ocultos/contexto";

type LanceSummary = {
  contact_id: number;
  total_with_next: number;
  lance_ok: number;
  lance_registered: number;
  lance_already_placed: number;
  lance_cancelled: number;
  lance_failed: number;
  lance_missing: number;
};

type VendaFlagRow = {
  id: number;
  contact_id: number | null;
  contrato: string | null;
  status: string | null;
};
type InadimpRow = { id: number; contrato: string | null };
/** Cota que entra no "X pendentes": confirmada, com próxima assembleia e sem lance registrado. */
export type CotaPendente = { id: string; contact_id: number; cota: string | null; next_assembleia_date: string | null };

const dataCurta = (iso: string | null) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : "");

/** As linhas do balão do "X pendentes": até 5 cotas e o resto resumido. */
export const linhasDoBalao = (cotas: CotaPendente[]) => {
  const ordenadas = [...cotas].sort((a, b) => (a.next_assembleia_date ?? "").localeCompare(b.next_assembleia_date ?? ""));
  const linhas = ordenadas
    .slice(0, 5)
    .map((c) => `Cota ${c.cota ?? "sem número"}${c.next_assembleia_date ? ` · assembleia ${dataCurta(c.next_assembleia_date)}` : ""}`);
  const resto = ordenadas.length - 5;
  if (resto > 0) linhas.push(`+ ${resto} ${resto === 1 ? "outra cota" : "outras cotas"}`);
  return linhas;
};
type PersistenciaFlags = { hasCancelled: boolean; hasInadimp: boolean };

/**
 * Compact aggregate lance badge for contact list. Renders nothing if the
 * contact has no upcoming-assembleia cotas. Worst-status wins (failed > missing
 * > cancelled > ok).
 *
 * As contagens passam pelo olhinho: em Clientes (que tem provedor) viram
 * "•••• falhas"; em Contatos, sem provedor, ficam como sempre.
 */
const ContactLanceAggregate = ({ summary, pendentes }: { summary: LanceSummary; pendentes?: CotaPendente[] }) => {
  const m = useMascara();
  if (!summary || summary.total_with_next === 0) return null;
  const { lance_ok, lance_registered, lance_already_placed, lance_cancelled, lance_failed, lance_missing, total_with_next } = summary;
  // Categorias UI (4 buckets): OK (positivo), Pendente, Falha, Não disponível
  const totalOk = lance_ok + lance_registered + lance_already_placed;

  let label: string;
  let classes: string;
  let tooltip: string;
  // Prioridade: falha > pendente > não disponível > ok
  if (lance_failed > 0) {
    label = `${lance_failed} falha${lance_failed > 1 ? 's' : ''}`;
    classes = 'bg-red-100 text-red-800 border-red-200';
    tooltip = `${lance_failed} cota(s) com falha · ${totalOk}/${total_with_next} realizado(s)`;
  } else if (lance_missing > 0) {
    label = `${lance_missing} pendente${lance_missing > 1 ? 's' : ''}`;
    classes = 'bg-slate-100 text-slate-700 border-slate-200';
    tooltip = `${lance_missing} cota(s) pendente(s) · ${totalOk}/${total_with_next} realizado(s)`;
  } else if (lance_cancelled > 0 && totalOk === 0) {
    label = `${lance_cancelled} indisp.`;
    classes = 'bg-amber-100 text-amber-800 border-amber-200';
    tooltip = `${lance_cancelled} cota(s) indisponível(is): contemplada, somente sorteio ou outro motivo`;
  } else {
    label = `Lance: ${totalOk}/${total_with_next}`;
    classes = 'bg-green-100 text-green-800 border-green-200';
    tooltip = `${totalOk}/${total_with_next} cota(s) com lance realizado`;
  }

  const etiqueta = (
    <span
      title={pendentes?.length ? undefined : m(tooltip)}
      className={`inline-block whitespace-nowrap rounded-[4px] border px-1.5 py-0.5 text-[10.5px] font-semibold ${classes}`}
    >
      {m(label)}
    </span>
  );
  // "X pendentes" (pedido do Pedro, 28/10/2026): o balão lista as MESMAS cotas
  // que a contagem — confirmadas, com próxima assembleia e sem lance (view
  // contacts_lance_summary, coluna lance_missing).
  if (lance_failed > 0 || lance_missing === 0 || !pendentes?.length) return etiqueta;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>{etiqueta}</TooltipTrigger>
        <TooltipContent className="max-w-72 text-left">
          <p className="mb-1 font-semibold">Lance pendente para a próxima assembleia:</p>
          <ul className="space-y-0.5">
            {linhasDoBalao(pendentes).map((l) => (
              <li key={l}>• {m(l)}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const GRADE =
  "grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 @3xl:grid-cols-[auto_minmax(0,1fr)_10rem_13rem_9rem]";

export const ContactListContent = ({ showPersistencia = false }: { showPersistencia?: boolean } = {}) => {
  const { somenteLeitura } = useSomenteLeitura();
  const ehSeguros = useProductModule() === "insurance";
  const translate = useTranslate();
  const dateFnsLocale = useDateFnsLocale();
  const {
    data: contacts,
    error,
    isPending,
    onToggleItem,
    selectedIds,
  } = useListContext<Contact>();

  // Batch-fetch lance summaries for visible contacts (one extra request,
  // not N+1) so we can render an aggregate badge per row.
  const visibleIds = useMemo(
    () => (contacts ?? []).map((c) => c.id),
    [contacts],
  );
  // Sem genérico: a view contacts_lance_summary não tem coluna `id`, então não
  // satisfaz RaRecord. O afunilamento acontece na leitura abaixo.
  // Sem `sort` o provider ordena por `id` e o PostgREST recusa (400); a lista
  // vai em `@in` porque um array puro vira `eq.1,2,3`.
  const { data: lanceSummaries } = useGetList(
    "contacts_lance_summary",
    {
      filter: { "contact_id@in": `(${visibleIds.join(",")})` },
      pagination: { page: 1, perPage: 200 },
      sort: { field: "contact_id", order: "ASC" },
    },
    { enabled: visibleIds.length > 0 },
  );
  const summaryByContact = useMemo(() => {
    const map = new Map<number, LanceSummary>();
    for (const s of (lanceSummaries ?? []) as unknown as LanceSummary[]) map.set(s.contact_id, s);
    return map;
  }, [lanceSummaries]);

  // As cotas por trás do "X pendentes", numa leitura só para a página inteira.
  const idsComPendente = useMemo(
    () => (lanceSummaries ?? []).filter((s) => Number((s as unknown as LanceSummary).lance_missing) > 0).map((s) => (s as unknown as LanceSummary).contact_id),
    [lanceSummaries],
  );
  const { data: cotasPendentesRows } = useGetList(
    "servopa_vendas_with_details",
    {
      filter: { "contact_id@in": `(${idsComPendente.join(",")})`, status: "confirmed", "next_lance_status@is": null },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: idsComPendente.length > 0 },
  );
  const pendentesByContact = useMemo(() => {
    const map = new Map<number, CotaPendente[]>();
    for (const c of (cotasPendentesRows ?? []) as unknown as CotaPendente[]) {
      if (!c.next_assembleia_date) continue;
      map.set(c.contact_id, [...(map.get(c.contact_id) ?? []), c]);
    }
    return map;
  }, [cotasPendentesRows]);

  // Persistência: pra cada contato visível, marca se tem venda cancelada
  // (vermelho) e/ou cota inadimplente ativa (laranja). Duas queries batch
  // — não escala em N+1. Só aparece quando showPersistencia=true (aba
  // Clientes; em Contatos esconde).
  const { data: vendasForContacts } = useGetList<VendaFlagRow>(
    "servopa_vendas",
    {
      filter: { contact_id: visibleIds, "status@in": "(cancelled,confirmed)" },
      pagination: { page: 1, perPage: 1000 },
    },
    { enabled: showPersistencia && visibleIds.length > 0 },
  );
  const { data: inadimpsRows } = useGetList<InadimpRow>(
    "servopa_inadimplentes_current",
    { pagination: { page: 1, perPage: 5000 } },
    { enabled: showPersistencia },
  );
  const persistenciaByContact = useMemo(() => {
    const inadimpContratos = new Set<string>();
    for (const i of inadimpsRows ?? []) {
      if (i.contrato) inadimpContratos.add(i.contrato);
    }
    const out = new Map<number, PersistenciaFlags>();
    for (const v of vendasForContacts ?? []) {
      if (v.contact_id == null) continue;
      let entry = out.get(v.contact_id);
      if (!entry) { entry = { hasCancelled: false, hasInadimp: false }; out.set(v.contact_id, entry); }
      if (v.status === "cancelled") entry.hasCancelled = true;
      else if (v.status === "confirmed" && v.contrato && inadimpContratos.has(v.contrato)) {
        entry.hasInadimp = true;
      }
    }
    return out;
  }, [vendasForContacts, inadimpsRows]);

  // StopPropagation does not work for some reason on Checkbox, this handler is a workaround
  const handleLinkClick = useCallback(function handleLinkClick(
    e: MouseEvent<HTMLAnchorElement>,
  ) {
    if (e.target instanceof HTMLButtonElement) {
      e.preventDefault();
    }
  }, []);

  if (isPending) {
    return <PageSpinner />;
  }

  if (error) {
    return null;
  }
  const now = Date.now();

  return (
    // Container query (largura do cartão, não da tela): na largura boa são as
    // colunas de sempre; quando aperta, telefone, e-mail e última atividade
    // descem para baixo do nome em vez de empurrar a tabela para o lado.
    <div className="@container divide-y divide-border">
      {/* Cabeçalho claro (Premium North) */}
      <div className={`hidden px-4 py-2 text-xs font-medium @3xl:grid ${GRADE} ${cabecalhoTabelaNorth}`}>
        <div className={somenteLeitura ? "" : "w-4"} />
        <div>Nome</div>
        <div>Telefone</div>
        <div>E-mail</div>
        <div className="text-right">Última atividade</div>
      </div>
      {contacts.map((contact) => {
        const phone = getContactDetail(contact, "phone") as string | undefined;
        const email = getContactDetail(contact, "email") as string | undefined;
        const ultimaAtividade = contact.last_seen
          ? formatRelative(contact.last_seen, now, { locale: dateFnsLocale })
          : null;

        return (
          <RecordContextProvider key={contact.id} value={contact}>
            <Link
              to={`/contacts/${contact.id}/show`}
              className={`${GRADE} gap-y-1 px-4 py-3 transition-colors hover:bg-linha-hover hover:shadow-[inset_3px_0_0_var(--marinho)] dark:hover:bg-muted/50 dark:hover:shadow-none`}
              onClick={handleLinkClick}
            >
              <div className={somenteLeitura ? "" : "w-4"}>
                {!somenteLeitura && (
                  <Checkbox
                    className="cursor-pointer shrink-0"
                    checked={selectedIds.includes(contact.id)}
                    onCheckedChange={() => onToggleItem(contact.id)}
                  />
                )}
              </div>
              {/* Nome */}
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold text-marinho">
                  <span className="truncate">{getContactDisplayName(contact)}</span>
                  {summaryByContact.get(Number(contact.id)) && (
                    <ContactLanceAggregate
                      summary={summaryByContact.get(Number(contact.id))!}
                      pendentes={pendentesByContact.get(Number(contact.id))}
                    />
                  )}
                  {showPersistencia && (() => {
                    const p = persistenciaByContact.get(Number(contact.id));
                    if (!p) return null;
                    return (
                      <>
                        {p.hasInadimp && (
                          <span
                            title="Cliente com cota inadimplente: risco de cancelamento"
                            className="inline-block whitespace-nowrap rounded-[4px] bg-orange-100 px-1.5 py-0.5 text-[10.5px] font-semibold text-orange-800"
                          >
                            inadimplente
                          </span>
                        )}
                        {p.hasCancelled && (
                          <span
                            title="Cliente teve venda cancelada"
                            className="inline-block whitespace-nowrap rounded-[4px] bg-erro-fundo px-1.5 py-0.5 text-[10.5px] font-semibold text-erro"
                          >
                            cancelado
                          </span>
                        )}
                      </>
                    );
                  })()}
                </div>
                {/* Temperatura (280): só no consórcio; seguros fica de fora (cartão do Pedro). */}
                {!ehSeguros && (
                  <div className="mt-0.5"><EtiquetaTemperatura valor={contact.temperatura} /></div>
                )}
              </div>
              {/* Telefone, e-mail e última atividade: na largura boa são colunas
                  (o "contents" solta os três na grade); quando aperta, descem para
                  baixo do nome numa linha só. Um elemento por dado, nunca dois. */}
              <div className="col-start-2 flex min-w-0 flex-wrap gap-x-3 text-xs text-muted-foreground @3xl:contents">
                <div className="truncate @3xl:text-sm">
                  {phone || <span className="text-muted-foreground/40">—</span>}
                </div>
                <div className="min-w-0 truncate @3xl:text-sm">
                  {email || <span className="text-muted-foreground/40">—</span>}
                </div>
                <div className="@3xl:text-right">
                  {ultimaAtividade}
                </div>
              </div>
            </Link>
          </RecordContextProvider>
        );
      })}

      {contacts.length === 0 && (
        <div className="p-4">
          <div className="text-muted-foreground">{translate("atomic.contacts.noContactsFound")}</div>
        </div>
      )}
    </div>
  );
};
